import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import HomeIcon from "@mui/icons-material/Home";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Grow from "@mui/material/Grow";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { toast } from "react-toastify";
import instance from "../../axios";
import { errorHandler } from "../../Helpers";
import { loadQuestionData } from "./api";
import { isComingSoon, isEmptyResult } from "./format";
import { CATEGORIES, PROMPTS, QUESTIONS } from "./questions";
import {
  canAccessCategory,
  canAccessQuestion,
  getAllowedCategories,
  getAllowedQuestions,
} from "./privileges";
import ResultView from "./ResultView";

const LEVEL = {
  WELCOME: "welcome",
  CATEGORIES: "categories",
  QUESTIONS: "questions",
  VEHICLE_PICKER: "vehiclePicker",
  RESULT: "result",
  CUSTOM_INQUIRY: "customInquiry",
};

const EMPTY_INQUIRY = { subject: "", message: "" };

const CATEGORY_ICONS = {
  vehicles: DirectionsCarOutlinedIcon,
  services: BuildOutlinedIcon,
  invoices: ReceiptLongOutlinedIcon,
  deliveryNotes: LocalShippingOutlinedIcon,
  savings: SavingsOutlinedIcon,
  customInquiry: SupportAgentOutlinedIcon,
};

const categoryLabel = (id, translate) => {
  const item = CATEGORIES.find((cat) => cat.id === id);
  return item ? translate(item.labelKey) : id;
};

const questionLabel = (categoryId, questionId, translate) => {
  const question = (QUESTIONS[categoryId] || []).find((item) => item.id === questionId);
  return question ? translate(question.labelKey) : questionId;
};

const choiceSx = (accent) => ({
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  px: 1.5,
  py: 1.05,
  bgcolor: "background.paper",
  "&:hover": {
    borderColor: accent,
    bgcolor: alpha(accent || "#1E72B8", 0.06),
  },
});

const StatusNote = ({ children, retryLabel, onRetry, severity = "info" }) => (
  <Stack spacing={1.25} className="fleetbot-empty" sx={{ py: 3 }}>
    <Alert severity={severity} variant="outlined" sx={{ textAlign: "left" }}>
      {children}
    </Alert>
    {onRetry ? (
      <Button variant="contained" size="small" onClick={onRetry}>
        {retryLabel}
      </Button>
    ) : null}
  </Stack>
);

const ChatPanel = ({
  open,
  onClose,
  themecolors,
  translate,
  currency,
  liveContext = {},
}) => {
  const dispatch = useDispatch();
  const [level, setLevel] = useState(LEVEL.WELCOME);
  const [categoryId, setCategoryId] = useState(null);
  const [questionId, setQuestionId] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [vehicleQuery, setVehicleQuery] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inquiry, setInquiry] = useState(EMPTY_INQUIRY);
  const [inquirySending, setInquirySending] = useState(false);
  const [inquirySent, setInquirySent] = useState(false);
  const [inquiryError, setInquiryError] = useState("");
  const requestId = useRef(0);
  const accent = themecolors.themeLightColor || "#1E72B8";

  const reset = () => {
    requestId.current += 1;
    setLevel(LEVEL.WELCOME);
    setCategoryId(null);
    setQuestionId(null);
    setVehicle(null);
    setVehicleOptions([]);
    setVehicleQuery("");
    setData(null);
    setLoading(false);
    setError("");
    setInquiry(EMPTY_INQUIRY);
    setInquirySending(false);
    setInquirySent(false);
    setInquiryError("");
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  const crumbs = useMemo(() => {
    const items = [{ id: "home", label: translate("fleetBot") }];
    if (categoryId) {
      items.push({ id: "category", label: categoryLabel(categoryId, translate) });
    }
    if (questionId) {
      items.push({
        id: "question",
        label: questionLabel(categoryId, questionId, translate),
      });
    }
    if (vehicle && questionId === "vehicleDetails") {
      items.push({
        id: "vehicle",
        label: vehicle.plateNumber || vehicle.name,
      });
    }
    return items;
  }, [categoryId, questionId, vehicle, translate]);

  const promptKey =
    level === LEVEL.WELCOME
      ? PROMPTS.welcome
      : level === LEVEL.CATEGORIES
      ? PROMPTS.categories
      : level === LEVEL.VEHICLE_PICKER
      ? PROMPTS.vehiclePicker
      : level === LEVEL.CUSTOM_INQUIRY
      ? PROMPTS.customInquiry
      : level === LEVEL.QUESTIONS
      ? PROMPTS[categoryId]
      : null;

  const fetchResult = async (nextQuestionId, nextVehicleId) => {
    const id = ++requestId.current;
    setLoading(true);
    setError("");
    setData(null);
    try {
      const result = await loadQuestionData(nextQuestionId, nextVehicleId, {
        ...liveContext,
        vehicleOptions,
      });
      if (id !== requestId.current) return;
      setData(result);
    } catch (err) {
      if (id !== requestId.current) return;
      setError(err?.message || translate("fleetBotLoadError"));
    } finally {
      if (id === requestId.current) {
        setLoading(false);
      }
    }
  };

  const goToCategories = () => {
    requestId.current += 1;
    setLevel(LEVEL.CATEGORIES);
    setCategoryId(null);
    setQuestionId(null);
    setVehicle(null);
    setVehicleOptions([]);
    setVehicleQuery("");
    setData(null);
    setError("");
    setLoading(false);
  };

  const goHome = () => {
    reset();
  };

  const goBack = () => {
    requestId.current += 1;
    setLoading(false);
    if (level === LEVEL.CUSTOM_INQUIRY) {
      setCategoryId(null);
      setInquiry(EMPTY_INQUIRY);
      setInquirySending(false);
      setInquirySent(false);
      setInquiryError("");
      setLevel(LEVEL.CATEGORIES);
      return;
    }
    if (level === LEVEL.RESULT && questionId === "vehicleDetails" && vehicle) {
      setVehicle(null);
      setData(null);
      setError("");
      setLevel(LEVEL.VEHICLE_PICKER);
      return;
    }
    if (level === LEVEL.RESULT || level === LEVEL.VEHICLE_PICKER) {
      setQuestionId(null);
      setVehicle(null);
      setData(null);
      setError("");
      setLevel(LEVEL.QUESTIONS);
      return;
    }
    if (level === LEVEL.QUESTIONS) {
      setCategoryId(null);
      setLevel(LEVEL.CATEGORIES);
      return;
    }
    if (level === LEVEL.CATEGORIES) {
      reset();
      return;
    }
    if (level === LEVEL.WELCOME) {
      onClose();
    }
  };

  const openCategory = (item) => {
    const id = typeof item === "string" ? item : item?.id;
    const category =
      typeof item === "object" && item
        ? item
        : CATEGORIES.find((cat) => cat.id === id);

    if (!canAccessCategory(id)) {
      setError(translate("somethingWentWrong") || "Access denied");
      return;
    }

    if (category?.isForm || id === "customInquiry") {
      setCategoryId("customInquiry");
      setQuestionId(null);
      setVehicle(null);
      setInquiry(EMPTY_INQUIRY);
      setInquirySending(false);
      setInquirySent(false);
      setInquiryError("");
      setLevel(LEVEL.CUSTOM_INQUIRY);
      return;
    }

    setCategoryId(id);
    setQuestionId(null);
    setVehicle(null);
    setLevel(LEVEL.QUESTIONS);
  };

  const setInquiryField = (name, value) => {
    setInquiry((prev) => ({ ...prev, [name]: value }));
    setInquiryError("");
    setInquirySent(false);
  };

  const canSubmitInquiry =
    Boolean(inquiry.subject?.trim()) && Boolean(inquiry.message?.trim()) && !inquirySending;

  const submitInquiry = async () => {
    if (!canSubmitInquiry) return;
    const user = liveContext.logInUser || {};
    setInquirySending(true);
    setInquiryError("");
    try {
      await instance({
        url: `api/feedbacks`,
        method: `POST`,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        data: {
          name: user.name || "",
          email: user.email || "",
          subject: inquiry.subject.trim(),
          contact: user.phone || "",
          country: user.country || "",
          message: inquiry.message.trim(),
        },
      });
      toast.success(translate("emailSentSuccessfully"));
      setInquiry(EMPTY_INQUIRY);
      setInquirySent(true);
    } catch (err) {
      errorHandler(err, dispatch);
      setInquiryError(translate("fleetBotLoadError"));
    } finally {
      setInquirySending(false);
    }
  };

  const openQuestion = async (question) => {
    if (!canAccessQuestion(question.id)) {
      setError(translate("somethingWentWrong") || "Access denied");
      setLevel(LEVEL.RESULT);
      return;
    }
    setQuestionId(question.id);
    setVehicle(null);
    setData(null);
    setError("");
    if (question.comingSoon) {
      setLevel(LEVEL.RESULT);
      setLoading(false);
      setData({ comingSoon: true });
      return;
    }
    if (question.needsVehicle) {
      setLevel(LEVEL.VEHICLE_PICKER);
      setLoading(true);
      setError("");
      try {
        const result = await loadQuestionData("vehiclePicker", null, liveContext);
        setVehicleOptions(result.vehicles || []);
      } catch (err) {
        setError(err?.message || translate("fleetBotLoadError"));
      } finally {
        setLoading(false);
      }
      return;
    }
    setLevel(LEVEL.RESULT);
    fetchResult(question.id);
  };

  const selectVehicle = (item) => {
    setVehicle(item);
    setLevel(LEVEL.RESULT);
    fetchResult("vehicleDetails", item);
  };

  const filteredVehicles = vehicleOptions.filter((item) => {
    const q = vehicleQuery.trim().toLowerCase();
    if (!q) return true;
    return `${item.name} ${item.plateNumber}`.toLowerCase().includes(q);
  });

  const loadingRow = (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      justifyContent="center"
      className="fleetbot-status"
    >
      <CircularProgress size={18} sx={{ color: accent }} />
      <Typography variant="body2" color="text.secondary">
        {translate("fleetBotLoading")}
      </Typography>
    </Stack>
  );

  return (
    <Grow in={open} style={{ transformOrigin: "bottom right" }}>
      <Paper
        className={`fleetbot-panel ${open ? "is-open" : ""}`}
        elevation={16}
        role="dialog"
        aria-label={translate("fleetBot")}
        aria-hidden={!open}
        sx={{
          bgcolor: themecolors.backgroundColor || "#fff",
          color: themecolors.textColor,
          overflow: "hidden",
        }}
      >
        <Box
          className="fleetbot-header"
          sx={{
            background: `linear-gradient(135deg, ${themecolors.themeDarkColor} 0%, ${accent} 160%)`,
            color: themecolors.themeInverse,
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Avatar
              className="fleetbot-avatar"
              sx={{
                width: 34,
                height: 34,
                bgcolor: "rgba(255,255,255,0.16)",
                color: "inherit",
              }}
            >
              <SmartToyOutlinedIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
                {translate("fleetBot")}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.82 }}>
                {translate("fleetBotAssistant")}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={translate("sharedCancel") || "Close"}
            sx={{ color: "inherit" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          className="fleetbot-nav"
        >
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={goBack}
            sx={{ color: "text.primary", minWidth: 0, textTransform: "none" }}
          >
            {translate("sharedBack")}
          </Button>
          <Button
            size="small"
            startIcon={<HomeIcon />}
            onClick={goHome}
            sx={{ color: "text.primary", minWidth: 0, textTransform: "none" }}
          >
            {translate("fleetBotHome")}
          </Button>
          <Breadcrumbs
            separator="→"
            className="fleetbot-crumbs"
            title={crumbs.map((item) => item.label).join(" → ")}
            sx={{
              fontSize: 11,
              color: "text.secondary",
              minWidth: 0,
              "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap" },
              "& .MuiBreadcrumbs-li": { minWidth: 0 },
            }}
          >
            {crumbs.map((item) => (
              <Typography key={item.id} variant="caption" noWrap>
                {item.label}
              </Typography>
            ))}
          </Breadcrumbs>
        </Stack>

        <Box className="fleetbot-body">
          <Box key={level} className="fleetbot-stage">
            {promptKey ? (
              <Paper
                elevation={0}
                className="fleetbot-bubble"
                sx={{
                  bgcolor: alpha(accent, 0.08),
                  borderRadius: "12px 12px 12px 4px",
                }}
              >
                <Typography variant="body2">
                  {level === LEVEL.WELCOME ? (
                    <>
                      <strong>{translate("fleetBot")}</strong>
                      <span> {translate(promptKey)}</span>
                    </>
                  ) : (
                    translate(promptKey)
                  )}
                </Typography>
              </Paper>
            ) : null}

            {level === LEVEL.WELCOME ? (
              <Stack className="fleetbot-choices" spacing={1}>
                <Button
                  className="fleetbot-choice"
                  variant="contained"
                  fullWidth
                  onClick={goToCategories}
                  sx={{
                    bgcolor: accent,
                    color: themecolors.themeLightInverse,
                    textTransform: "none",
                    py: 1.1,
                    "&:hover": { bgcolor: accent, filter: "brightness(1.06)" },
                  }}
                >
                  {translate("fleetBotYesShowOptions")}
                </Button>
                <Button
                  className="fleetbot-choice"
                  variant="outlined"
                  fullWidth
                  onClick={onClose}
                  sx={{ textTransform: "none", py: 1.1, borderColor: "divider" }}
                >
                  {translate("fleetBotNoClose")}
                </Button>
              </Stack>
            ) : null}

            {level === LEVEL.CATEGORIES ? (
              <Stack className="fleetbot-choices" spacing={1}>
                {getAllowedCategories().map((item) => {
                  const Icon = CATEGORY_ICONS[item.id] || ChevronRightIcon;
                  return (
                    <ListItemButton
                      key={item.id}
                      className="fleetbot-choice"
                      onClick={() => openCategory(item)}
                      sx={choiceSx(accent)}
                    >
                      <ListItemIcon sx={{ minWidth: 42 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: alpha(accent, 0.12),
                            color: accent,
                          }}
                        >
                          <Icon fontSize="small" />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={translate(item.labelKey)}
                        primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                      />
                      <ChevronRightIcon fontSize="small" sx={{ opacity: 0.45 }} />
                    </ListItemButton>
                  );
                })}
              </Stack>
            ) : null}

            {level === LEVEL.QUESTIONS ? (
              <Stack className="fleetbot-choices" spacing={1}>
                {getAllowedQuestions(categoryId).map((item) => (
                  <ListItemButton
                    key={item.id}
                    className="fleetbot-choice"
                    onClick={() => openQuestion(item)}
                    sx={choiceSx(accent)}
                  >
                    <ListItemText
                      primary={translate(item.labelKey)}
                      primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                    />
                    {item.comingSoon ? (
                      <Chip
                        size="small"
                        label={translate("fleetBotComingSoon")}
                        sx={{ height: 20, fontSize: 10, mr: 0.5 }}
                      />
                    ) : null}
                    <ChevronRightIcon fontSize="small" sx={{ opacity: 0.45 }} />
                  </ListItemButton>
                ))}
              </Stack>
            ) : null}

            {level === LEVEL.CUSTOM_INQUIRY ? (
              <Stack className="fleetbot-inquiry" spacing={1.5} sx={{ pt: 0.5 }}>
                <TextField
                  id="fleetbot-inquiry-subject"
                  label={translate("ticketSubject")}
                  value={inquiry.subject}
                  onChange={(e) => setInquiryField("subject", e.target.value)}
                  required
                  fullWidth
                  size="small"
                  autoComplete="off"
                />
                <TextField
                  id="fleetbot-inquiry-message"
                  label={translate("message")}
                  value={inquiry.message}
                  onChange={(e) => setInquiryField("message", e.target.value)}
                  required
                  fullWidth
                  multiline
                  minRows={4}
                  size="small"
                  autoComplete="off"
                />
                {inquiryError ? (
                  <Alert severity="error" variant="outlined">
                    {inquiryError}
                  </Alert>
                ) : null}
                {inquirySent ? (
                  <Alert severity="success" variant="outlined">
                    {translate("emailSentSuccessfully")}
                  </Alert>
                ) : null}
                <Button
                  variant="contained"
                  onClick={submitInquiry}
                  disabled={!canSubmitInquiry}
                  sx={{
                    alignSelf: "stretch",
                    textTransform: "none",
                    fontWeight: 600,
                    bgcolor: accent,
                    "&:hover": { bgcolor: accent, filter: "brightness(1.05)" },
                  }}
                >
                  {inquirySending ? translate("fleetBotLoading") : translate("submit")}
                </Button>
              </Stack>
            ) : null}

            {level === LEVEL.VEHICLE_PICKER ? (
              <>
                {!loading && !error ? (
                  <TextField
                    className="fleetbot-search"
                    value={vehicleQuery}
                    onChange={(event) => setVehicleQuery(event.target.value)}
                    placeholder={translate("search")}
                    autoComplete="off"
                    size="small"
                    fullWidth
                    sx={{ mb: 1.25 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : null}
                {loading ? loadingRow : null}
                {error ? (
                  <StatusNote
                    severity="error"
                    retryLabel={translate("fleetBotTryAgain")}
                    onRetry={() =>
                      openQuestion({ id: "vehicleDetails", needsVehicle: true })
                    }
                  >
                    {translate("fleetBotLoadError")}
                  </StatusNote>
                ) : null}
                {!loading && !error ? (
                  <Stack className="fleetbot-choices" spacing={1}>
                    {filteredVehicles.length ? (
                      filteredVehicles.map((item) => (
                        <ListItemButton
                          key={item.id}
                          className="fleetbot-choice"
                          onClick={() => selectVehicle(item)}
                          sx={choiceSx(accent)}
                        >
                          <ListItemText
                            primary={item.name}
                            secondary={item.plateNumber}
                            primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                            secondaryTypographyProps={{ fontSize: 11 }}
                          />
                          <ChevronRightIcon fontSize="small" sx={{ opacity: 0.45 }} />
                        </ListItemButton>
                      ))
                    ) : (
                      <StatusNote>{translate("fleetBotNoData")}</StatusNote>
                    )}
                  </Stack>
                ) : null}
              </>
            ) : null}

            {level === LEVEL.RESULT ? (
              <>
                {loading ? loadingRow : null}
                {error ? (
                  <StatusNote
                    severity="error"
                    retryLabel={translate("fleetBotTryAgain")}
                    onRetry={() => fetchResult(questionId, vehicle)}
                  >
                    {translate("fleetBotLoadError")}
                  </StatusNote>
                ) : null}
                {!loading && !error && isComingSoon(data) ? (
                  <StatusNote>{translate("fleetBotComingSoon")}</StatusNote>
                ) : null}
                {!loading &&
                !error &&
                !isComingSoon(data) &&
                isEmptyResult(questionId, data) ? (
                  <StatusNote>{translate("fleetBotNoData")}</StatusNote>
                ) : null}
                {!loading &&
                !error &&
                data &&
                !isComingSoon(data) &&
                !isEmptyResult(questionId, data) ? (
                  <Box className="fleetbot-result">
                    <ResultView
                      questionId={questionId}
                      data={data}
                      translate={translate}
                      currency={currency}
                    />
                  </Box>
                ) : null}
              </>
            ) : null}
          </Box>
        </Box>
      </Paper>
    </Grow>
  );
};

export default ChatPanel;
